/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent: dueño arma Tamaño/Extras entre turnos (pocos grupos; sin paginar).
 * Hierarchy: hero Carta → cards de grupo → modal (strip + opciones | aplicar a).
 * Depth: cards Square (borde stone) + ModalShell.
 * Signature: opción = línea de comanda; lista = meta corta + chips.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../../shared/theme/colors'
import { ticketColors } from '../../shared/theme/ticketColors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { matchesSearchText } from '../../shared/format/searchText';
import { CONTENT_WIDE_BREAKPOINT, WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showInfo, showSuccess } from '../../shared/ui/appToast'
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { ModalShell } from '../../shared/modals/ModalShell'
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalNestedPicker, type ModalPickerOption } from '../../shared/modals/ModalNestedPicker'
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  listOrgOptionGroups,
  setOptionGroupActive,
  upsertOptionGroup,
  type OrgOptionGroup,
} from '../infra/menuModifiersRepo';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useMenuModifiersEnabled } from '../application/useMenuModifiersEnabled';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { currencyFormatter } from './catalogScreen.constants';

type ProductPick = {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  parent_product_id?: string | null;
};

type ChoiceDraft = {
  key: string;
  id?: string;
  name: string;
  priceDelta: string;
  maxQty: string;
  linkedProductId: string;
  isAvailable: boolean;
  /** '' = automático (heurística por texto/precio). */
  modifierKind: '' | 'extra' | 'exclude' | 'detail';
  /** Separados por coma; mismo vocabulario que alérgenos de producto. */
  allergenTags: string;
  kitchenLabel: string;
  /** Solo apunta a opciones ya guardadas del mismo grupo. */
  substitutesChoiceId: string;
  /** Componente de combo (producto) que esta opción reemplaza. */
  substitutesProductId: string;
};

/**
 * Nombre de opción que ya trae «+$800» / «+800» al final (datos viejos o el
 * usuario lo tipeó en el nombre). El precio se muestra una sola vez y con el
 * formato canónico.
 */
const TRAILING_PRICE_TOKEN = /\s*\+\s*\$?\s*\d[\d.,]*\s*$/;

function formatChoicePreview(name: string, priceDelta: number) {
  const base = name.replace(TRAILING_PRICE_TOKEN, '').trim() || name.trim();
  return priceDelta === 0 ? base : `${base} +${currencyFormatter.format(priceDelta)}`;
}

function formatGroupMeta(group: OrgOptionGroup): { status: string; detail: string } {
  const required = group.selectionMin > 0;
  const same = group.selectionMin === group.selectionMax;
  const status = !group.isActive
    ? 'Inactivo'
    : required
      ? 'Obligatorio'
      : 'Opcional';
  const rule = !group.isActive
    ? 'No se ofrece'
    : required && same && group.selectionMin === 1
      ? 'elige 1'
      : required
        ? `elige ${group.selectionMin}–${group.selectionMax}`
        : same
          ? `hasta ${group.selectionMax}`
          : `${group.selectionMin}–${group.selectionMax}`;
  const n = group.productIds.length;
  const products = n === 0 ? 'sin productos' : `${n} producto${n === 1 ? '' : 's'}`;
  return { status, detail: `${rule} · ${products}` };
}

function newChoiceKey() {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Ancho del input +$: crece con dígitos, tope para no aplastar el switch. */
function deltaFieldInputWidth(value: string) {
  const len = Math.max(1, value.trim().length);
  return Math.min(92, Math.max(52, len * 11 + 4));
}

function defaultChoices(): ChoiceDraft[] {
  return [
    { key: newChoiceKey(), name: 'Simple', priceDelta: '0', maxQty: '1', linkedProductId: '', isAvailable: true, modifierKind: '', allergenTags: '', kitchenLabel: '', substitutesChoiceId: '', substitutesProductId: '' },
    { key: newChoiceKey(), name: 'Doble', priceDelta: '0', maxQty: '1', linkedProductId: '', isAvailable: true, modifierKind: '', allergenTags: '', kitchenLabel: '', substitutesChoiceId: '', substitutesProductId: '' },
    { key: newChoiceKey(), name: 'Triple', priceDelta: '0', maxQty: '1', linkedProductId: '', isAvailable: true, modifierKind: '', allergenTags: '', kitchenLabel: '', substitutesChoiceId: '', substitutesProductId: '' },
  ];
}

function draftsFromGroup(group: OrgOptionGroup): ChoiceDraft[] {
  if (!group.choices.length) return defaultChoices();
  return group.choices.map((c) => ({
    key: c.id,
    id: c.id,
    name: c.name,
    priceDelta: String(c.priceDelta ?? 0),
    maxQty: String(Math.max(1, c.maxQty || 1)),
    linkedProductId: c.linkedProductId ?? '',
    isAvailable: c.isAvailable !== false,
    modifierKind: c.modifierKind ?? '',
    allergenTags: (c.allergenTags ?? []).join(', '),
    kitchenLabel: c.kitchenLabel ?? '',
    substitutesChoiceId: c.substitutesChoiceId ?? '',
    substitutesProductId: c.substitutesProductId ?? '',
  }));
}

const SELECTION_PRESETS = [
  { id: 'req1', label: 'El cliente elige 1', shortLabel: 'Elige 1', min: '1', max: '1' },
  { id: 'opt', label: 'Opcional', shortLabel: 'Opcional', min: '0', max: '5' },
  { id: 'hasta', label: 'Elige hasta N', shortLabel: 'Hasta N', min: '0', max: '3' },
] as const;

const MODIFIER_KIND_ORDER = ['', 'extra', 'exclude', 'detail'] as const;
const MODIFIER_KIND_LABEL: Record<ChoiceDraft['modifierKind'], string> = {
  '': 'Auto',
  extra: 'Suma',
  exclude: 'Saca',
  detail: 'Detalle',
};

type ChoiceFieldPicker =
  | { choiceKey: string; field: 'linked' }
  | { choiceKey: string; field: 'substituteProduct' }
  | { choiceKey: string; field: 'substituteChoice' }
  | { choiceKey: string; field: 'modifierKind' };

export function MenuOptionGroupsScreen() {
  const navigation = useNavigation();
  const enabled = useMenuModifiersEnabled();
  const { entitlements } = usePlanEntitlements();
  const canViewMenuOptions = useModulePermission('MenuOptions', 'view');
  const canEditMenuOptions = useModulePermission('MenuOptions', 'edit');
  const canEditPrice = useModulePermission('MenuOptions', 'edit_price');
  const orgId = entitlements?.organization_id ?? null;
  const { width, height } = useWindowDimensions();
  const isWide = width >= CONTENT_WIDE_BREAKPOINT;
  // Misma línea que ModalShell bottom-sheet (<960): no meter 2 cols en el sheet.
  const isModalDesktop = width >= WEB_SIDEBAR_BREAKPOINT;
  const [groups, setGroups] = useState<OrgOptionGroup[]>([]);
  const [products, setProducts] = useState<ProductPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [name, setName] = useState('');
  const [choiceDrafts, setChoiceDrafts] = useState<ChoiceDraft[]>(defaultChoices);
  const [selectionMin, setSelectionMin] = useState('1');
  const [selectionMax, setSelectionMax] = useState('1');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingActive, setEditingActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [productFilter, setProductFilter] = useState('');
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [choiceFieldPicker, setChoiceFieldPicker] = useState<ChoiceFieldPicker | null>(null);
  const editorScrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    if (!editorOpen) setChoiceFieldPicker(null);
  }, [editorOpen]);

  useEffect(() => {
    if (!editorOpen) return;
    const t = setTimeout(() => {
      editorScrollRef.current?.scrollTo({ y: 0, animated: false });
    }, 40);
    return () => clearTimeout(t);
  }, [editorOpen, editingGroupId]);

  const load = useCallback(async () => {
    if (!enabled || !orgId) {
      setGroups([]);
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [gs, prodRes] = await Promise.all([
        listOrgOptionGroups(),
        // Filter org explicitly: RLS also exposes other orgs' public storefront products.
        // sku/barcode/parent_product_id: desambiguación cuando hay nombres repetidos.
        supabase
          .from('products')
          .select('id, name, sku, barcode, parent_product_id')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .or('is_misc.is.null,is_misc.eq.false')
          .is('deleted_at', null)
          .order('name')
          .limit(500),
      ]);
      if (prodRes.error) throw new Error(prodRes.error.message);
      setGroups(gs);
      setProducts((prodRes.data ?? []) as ProductPick[]);
    } catch (e) {
      presentUnexpectedFailure({ module: 'catalog', op: 'menu_option_groups.load', error: e, userMessage: 'No se pudieron cargar los grupos.' });
    } finally {
      setLoading(false);
    }
  }, [enabled, orgId]);

  const { refreshing, onRefresh } = useScreenLoadShell('MenuOptionGroups', async () => {
    await load();
  });

  const orderedProducts = useMemo(() => {
    const q = productFilter.trim();
    const base = q ? products.filter((p) => matchesSearchText(p.name, q)) : products;
    return [...base].sort((a, b) => {
      const aOn = selectedProductIds.has(a.id) ? 0 : 1;
      const bOn = selectedProductIds.has(b.id) ? 0 : 1;
      if (aOn !== bOn) return aOn - bOn;
      return a.name.localeCompare(b.name, 'es');
    });
  }, [products, productFilter, selectedProductIds]);

  /** Nombres repetidos (variantes hijas, productos homónimos) — cuántos hay. */
  const productNameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of products) counts.set(p.name, (counts.get(p.name) ?? 0) + 1);
    return counts;
  }, [products]);

  /**
   * Etiqueta de fila «Aplicar a»: si el nombre se repite, se agrega SKU /
   * código de barras / id corto para que las filas se distingan.
   */
  const productRowLabel = useCallback(
    (p: ProductPick): string => {
      if ((productNameCounts.get(p.name) ?? 0) <= 1) return p.name;
      const code = p.sku?.trim() || p.barcode?.trim();
      if (code) return `${p.name} · ${code}`;
      return `${p.name} · #${p.id.slice(-4)}`;
    },
    [productNameCounts],
  );

  const catalogProductPickerOptions = useMemo((): ModalPickerOption[] => {
    return products.map((p) => ({
      value: p.id,
      label: productRowLabel(p),
      subtitle: p.sku?.trim() || p.barcode?.trim() || undefined,
    }));
  }, [products, productRowLabel]);

  const choicePickerSheet = useMemo(() => {
    if (!choiceFieldPicker) return null;
    const draft = choiceDrafts.find((c) => c.key === choiceFieldPicker.choiceKey);
    if (!draft) return null;

    switch (choiceFieldPicker.field) {
      case 'linked':
        return {
          title: 'Insumo vinculado',
          selectedValue: draft.linkedProductId,
          options: [{ value: '', label: 'Sin vincular' }, ...catalogProductPickerOptions],
        };
      case 'substituteProduct':
        return {
          title: 'Componente que reemplaza',
          selectedValue: draft.substitutesProductId,
          options: [{ value: '', label: 'Ninguno' }, ...catalogProductPickerOptions],
        };
      case 'substituteChoice': {
        const candidates = choiceDrafts.filter((d) => d.id && d.key !== draft.key);
        return {
          title: 'Sustituye otra opción',
          selectedValue: draft.substitutesChoiceId,
          options: [
            { value: '', label: 'Ninguna' },
            ...candidates.map((d) => ({ value: d.id as string, label: d.name.trim() || 'Opción' })),
          ],
        };
      }
      case 'modifierKind':
        return {
          title: 'Tipo de opción',
          selectedValue: draft.modifierKind,
          options: MODIFIER_KIND_ORDER.map((k) => ({
            value: k,
            label: MODIFIER_KIND_LABEL[k],
          })),
        };
      default:
        return null;
    }
  }, [catalogProductPickerOptions, choiceDrafts, choiceFieldPicker]);

  const toggleProduct = (id: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectFilteredProducts = () => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      for (const p of orderedProducts) next.add(p.id);
      return next;
    });
  };

  const clearFilteredProducts = () => {
    const filteredIds = new Set(orderedProducts.map((p) => p.id));
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      for (const id of filteredIds) next.delete(id);
      return next;
    });
  };

  const updateChoice = (key: string, patch: Partial<ChoiceDraft>) => {
    setChoiceDrafts((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };

  const handleChoicePickerSelect = useCallback(
    (value: string) => {
      if (!choiceFieldPicker) return;
      const { choiceKey, field } = choiceFieldPicker;
      switch (field) {
        case 'linked':
          updateChoice(choiceKey, { linkedProductId: value });
          break;
        case 'substituteProduct':
          updateChoice(choiceKey, {
            substitutesProductId: value,
            ...(value ? { substitutesChoiceId: '' } : {}),
          });
          break;
        case 'substituteChoice':
          updateChoice(choiceKey, {
            substitutesChoiceId: value,
            ...(value ? { substitutesProductId: '' } : {}),
          });
          break;
        case 'modifierKind':
          updateChoice(choiceKey, {
            modifierKind: value as ChoiceDraft['modifierKind'],
          });
          break;
        default:
          break;
      }
      setChoiceFieldPicker(null);
    },
    [choiceFieldPicker],
  );

  const addChoice = () => {
    setChoiceDrafts((prev) => [
      ...prev,
      {
        key: newChoiceKey(),
        name: '',
        priceDelta: '0',
        maxQty: '1',
        linkedProductId: '',
        isAvailable: true,
        modifierKind: '',
        allergenTags: '',
        kitchenLabel: '',
        substitutesChoiceId: '',
        substitutesProductId: '',
      },
    ]);
  };

  const removeChoice = (key: string) => {
    setChoiceDrafts((prev) => (prev.length <= 1 ? prev : prev.filter((c) => c.key !== key)));
  };

  const openCreate = () => {
    if (!canEditMenuOptions) return;
    setEditingGroupId(null);
    setEditingActive(true);
    setName('');
    setSelectionMin('1');
    setSelectionMax('1');
    setChoiceDrafts(defaultChoices());
    setSelectedProductIds(new Set());
    setProductFilter('');
    setEditorOpen(true);
  };

  const openEdit = (group: OrgOptionGroup) => {
    if (!canViewMenuOptions) return;
    setEditingGroupId(group.id);
    setEditingActive(group.isActive);
    setName(group.name);
    setSelectionMin(String(group.selectionMin));
    setSelectionMax(String(group.selectionMax));
    setChoiceDrafts(draftsFromGroup(group));
    setSelectedProductIds(new Set(group.productIds));
    setProductFilter('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving || togglingActive || deactivateConfirmOpen) return;
    setEditorOpen(false);
  };

  /** Productos vinculados hoy (snapshot del servidor, no el draft del editor). */
  const editingGroup = editingGroupId
    ? groups.find((g) => g.id === editingGroupId) ?? null
    : null;
  const linkedProductCount = editingGroup?.productIds.length ?? 0;

  const runToggleActive = async () => {
    if (!editingGroupId || !canEditMenuOptions) return;
    setTogglingActive(true);
    try {
      const next = !editingActive;
      await setOptionGroupActive(editingGroupId, next);
      setEditingActive(next);
      setDeactivateConfirmOpen(false);
      await load();
      showSuccess('Listo', next ? 'Grupo reactivado' : 'Grupo desactivado');
    } catch (e) {
      presentUnexpectedFailure({ module: 'catalog', op: 'menu_option_groups.toggle_active', error: e, userMessage: 'No se pudo actualizar.' });
    } finally {
      setTogglingActive(false);
    }
  };

  /**
   * Desactivar con productos vinculados pide confirmación (con conteo de
   * afectados). Reactivar o desactivar sin productos va directo.
   */
  const handleToggleActive = () => {
    if (!editingGroupId || !canEditMenuOptions) return;
    if (editingActive && linkedProductCount > 0) {
      setDeactivateConfirmOpen(true);
      return;
    }
    void runToggleActive();
  };

  const handleSave = async () => {
    if (!canEditMenuOptions) {
      showInfo('Sin permiso', 'No tenés permiso para editar opciones de menú');
      return;
    }
    if (!name.trim()) {
      showInfo('Nombre', 'Poné un nombre (ej. Tamaño)');
      return;
    }
    const maxParsed = Number(selectionMax);
    const minParsed = Number(selectionMin);
    if (!Number.isFinite(maxParsed) || !Number.isFinite(minParsed)) {
      showInfo('Selección', 'Mínimo y máximo tienen que ser números.');
      return;
    }
    const max = Math.max(1, Math.floor(maxParsed));
    const minRaw = Math.max(0, Math.floor(minParsed));
    if (minRaw > max) {
      showInfo('Selección', 'El mínimo no puede ser mayor que el máximo.');
      return;
    }
    const min = minRaw;
    const existingById = editingGroupId
      ? new Map(
          (groups.find((g) => g.id === editingGroupId)?.choices ?? []).map((c) => [c.id, c]),
        )
      : null;

    for (const c of choiceDrafts) {
      if (!c.name.trim()) continue;
      if (canEditPrice) {
        const delta = Number(String(c.priceDelta).replace(',', '.'));
        if (!Number.isFinite(delta)) {
          showInfo('Precio', `En «${c.name.trim()}» el precio extra no es un número.`);
          return;
        }
        if (delta < 0) {
          showInfo('Precio', `En «${c.name.trim()}» el precio extra no puede ser negativo.`);
          return;
        }
      }
      const mq = Math.floor(Number(c.maxQty));
      if (!Number.isFinite(mq) || mq < 1) {
        showInfo('Máximo', `En «${c.name.trim()}» el máx por opción tiene que ser 1 o más.`);
        return;
      }
    }

    const choices = choiceDrafts
      .map((c, i) => {
        const prev = c.id && existingById ? existingById.get(c.id) : undefined;
        const rawDelta = Number(String(c.priceDelta).replace(',', '.'));
        const priceDelta = canEditPrice
          ? (Number.isFinite(rawDelta) ? rawDelta : 0)
          : Number(prev?.priceDelta ?? c.priceDelta) || 0;
        return {
          id: c.id,
          name: c.name.trim(),
          priceDelta,
          maxQty: Math.max(1, Math.floor(Number(c.maxQty) || 1)),
          linkedProductId: c.linkedProductId.trim() || null,
          isAvailable: c.isAvailable,
          sortOrder: i,
          modifierKind: c.modifierKind || null,
          allergenTags: c.allergenTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          kitchenLabel: c.kitchenLabel.trim() || null,
          // Sustitución choice↔choice y choice→componente son excluyentes (el
          // server también lo valida). Producto gana si el dueño puso ambos.
          substitutesChoiceId: c.substitutesProductId.trim()
            ? null
            : c.substitutesChoiceId.trim() || null,
          substitutesProductId: c.substitutesProductId.trim() || null,
        };
      })
      .filter((c) => c.name);
    if (choices.length === 0) {
      showInfo('Opciones', 'Agregá al menos una opción con nombre');
      return;
    }
    setSaving(true);
    try {
      await upsertOptionGroup({
        name: name.trim(),
        selectionMin: min,
        selectionMax: max,
        groupId: editingGroupId,
        choices,
        productIds: Array.from(selectedProductIds),
      });
      if (selectedProductIds.size === 0) {
        showSuccess(
          'Listo',
          editingGroupId
            ? 'Grupo actualizado. Sin productos, no aparece al vender.'
            : 'Grupo creado. Sin productos, no aparece al vender.',
        );
      } else {
        showSuccess('Listo', editingGroupId ? 'Grupo actualizado' : 'Grupo creado');
      }
      setEditorOpen(false);
      await load();
    } catch (e) {
      presentUnexpectedFailure({ module: 'catalog', op: 'menu_option_groups.save', error: e, userMessage: 'No se pudo guardar el grupo.' });
    } finally {
      setSaving(false);
    }
  };

  const renderProductRow = (p: ProductPick, index: number, total: number) => {
    const on = selectedProductIds.has(p.id);
    return (
      <TouchableOpacity
        key={p.id}
        style={[
          styles.productRow,
          index < total - 1 && styles.productRowBorder,
          on && styles.productRowOn,
        ]}
        onPress={() => toggleProduct(p.id)}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: on }}
        accessibilityLabel={productRowLabel(p)}
      >
        <View style={[styles.check, on && styles.checkOn]}>
          {on ? <Ionicons name="checkmark" size={14} color={colors.surface} /> : null}
        </View>
        <Text style={[styles.productName, on && styles.productNameOn]} numberOfLines={2}>
          {productRowLabel(p)}
        </Text>
      </TouchableOpacity>
    );
  };

  const matchedPreset = SELECTION_PRESETS.find(
    (p) => p.min === selectionMin && p.max === selectionMax,
  );
  const showMinMax = !matchedPreset || matchedPreset.id === 'hasta';

  const productListMaxH = Math.max(
    isModalDesktop ? 220 : 160,
    Math.round(height * (isModalDesktop ? 0.42 : 0.22)),
  );
  const productPicker = (
    <View style={[styles.panel, isModalDesktop ? styles.sideCol : undefined]}>
      <View style={styles.panelHead}>
        <Text style={styles.panelEyebrow}>Aplicar a</Text>
        <Text style={styles.panelCount}>
          {isModalDesktop
            ? `${selectedProductIds.size} elegidos`
            : String(selectedProductIds.size)}
        </Text>
      </View>
      <View style={styles.prodTools}>
        <TextInput
          style={[modalExperience.formInput, styles.searchInput]}
          value={productFilter}
          onChangeText={setProductFilter}
          placeholder="Buscar producto…"
          placeholderTextColor={colors.textMuted}
          clearButtonMode="while-editing"
        />
        {isModalDesktop ? (
          <View style={styles.bulkRow}>
            <TouchableOpacity
              style={styles.bulkBtn}
              onPress={selectFilteredProducts}
              accessibilityRole="button"
            >
              <Text style={styles.bulkBtnText}>Marcar estos</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.bulkBtn}
              onPress={clearFilteredProducts}
              accessibilityRole="button"
            >
              <Text style={styles.bulkBtnText}>Sacar estos</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
      <View style={[modalExperience.listContainer, styles.productList]}>
        {orderedProducts.length === 0 ? (
          <Text style={styles.emptyProducts}>No hay productos.</Text>
        ) : isModalDesktop ? (
          // Desktop: panel lateral con tope de alto propio.
          <ScrollView
            style={{ maxHeight: productListMaxH }}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            {orderedProducts.map((p, index) => renderProductRow(p, index, orderedProducts.length))}
          </ScrollView>
        ) : (
          // Sheet mobile: un solo scroll (el del modal) — sin lista anidada.
          <View>
            {orderedProducts.map((p, index) => renderProductRow(p, index, orderedProducts.length))}
          </View>
        )}
      </View>
    </View>
  );

  if (!enabled) {
    // Capability menu_modifiers apagada: el dueño salta directo a activarla
    // (mismo patrón que Combos) — sin dead-end.
    const isOwner = entitlements?.is_owner === true;
    return (
      <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
        <ModuleEmptyState
          variant="nest"
          title="Opciones de menú no disponibles"
          subtitle={
            isOwner
              ? 'Esta función está apagada. Activá Opciones de menú en Funciones del negocio.'
              : 'Este rubro no usa opciones de menú. El dueño puede activarlas desde Funciones del negocio.'
          }
          primaryAction={
            isOwner
              ? {
                  label: 'Abrir Funciones del negocio',
                  onPress: () => navigateInShell(navigation, 'BusinessFunctions'),
                  testID: 'capability.menu_modifiers.openFunctions',
                }
              : undefined
          }
          testID="capability.menu_modifiers.unavailable"
        />
      </ShellContentFrame>
    );
  }

  if (!canViewMenuOptions) {
    return (
      <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
        <ModuleEmptyState
          variant="nest"
          title="Opciones de menú no disponibles"
          subtitle="Pedile al dueño que te habilite Opciones de menú."
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
      <FlatList
        data={groups}
        keyExtractor={(g) => g.id}
        refreshing={refreshing}
        onRefresh={() => void onRefresh()}
        contentContainerStyle={styles.listContent}
        numColumns={isWide ? 2 : 1}
        key={isWide ? 'wide' : 'mobile'}
        columnWrapperStyle={isWide ? styles.listColumns : undefined}
        ListHeaderComponent={
          <View style={styles.listHeader}>
            <View style={styles.hero}>
              <View style={[styles.heroRow, !isWide && styles.heroRowStack]}>
                <View style={styles.heroCopy}>
                  <Text style={styles.heroKicker}>Catálogo</Text>
                  <Text style={styles.heroTitle}>Opciones de menú</Text>
                  <Text style={styles.heroSub}>
                    Tamaño, extras y lo que el cliente elige.
                  </Text>
                </View>
                {/* Sin grupos el CTA vive en el empty state («Crear el primer grupo»). */}
                {canEditMenuOptions && groups.length > 0 ? (
                  <TouchableOpacity
                    style={[styles.heroCta, !isWide && styles.heroCtaFull]}
                    onPress={openCreate}
                    accessibilityRole="button"
                    accessibilityLabel="Nuevo grupo"
                  >
                    <Ionicons name="add" size={18} color={colors.surface} />
                    <Text style={styles.heroCtaText}>Nuevo grupo</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              {groups.length > 0 ? (
                <Text style={styles.heroMeta}>
                  {groups.length} grupo{groups.length === 1 ? '' : 's'}
                </Text>
              ) : null}
            </View>
          </View>
        }
        ListEmptyComponent={
          loading ? (
            <ActivityIndicator color={colors.accent} style={{ marginTop: spacing.xl }} />
          ) : (
            <ModuleEmptyState
              variant="nest"
              title="Configurá tu carta"
              subtitle="Empezá con un grupo: Tamaño, Extras o Guarnición."
              primaryAction={
                canEditMenuOptions
                  ? { label: 'Crear el primer grupo', onPress: openCreate }
                  : undefined
              }
            />
          )
        }
        renderItem={({ item }) => {
          const chipCap = isWide ? 8 : 5;
          const meta = formatGroupMeta(item);
          const statusTone = !item.isActive
            ? 'danger'
            : item.selectionMin > 0
              ? 'warning'
              : 'neutral';
          return (
            <Pressable
              onPress={() => openEdit(item)}
              accessibilityRole="button"
              accessibilityLabel={`Editar grupo ${item.name}`}
              style={({ pressed }) => [
                styles.rowPress,
                isWide && styles.rowPressWide,
                pressed && styles.rowPressed,
              ]}
            >
              <View style={[styles.groupCard, !item.isActive && styles.groupCardOff]}>
                <View style={styles.rowTop}>
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {item.name}
                  </Text>
                  <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                </View>
                <View style={styles.metaRow}>
                  <StatusPill tone={statusTone} label={meta.status} compact />
                  <Text style={styles.metaText} numberOfLines={1}>
                    {meta.detail}
                  </Text>
                </View>
                {item.choices.length > 0 ? (
                  <View style={styles.choiceChipRow}>
                    {item.choices.slice(0, chipCap).map((c) => (
                      <View
                        key={c.id}
                        style={[styles.choiceChip, !c.isAvailable && styles.choiceChipOff]}
                      >
                        <Text style={styles.choiceChipText} numberOfLines={1}>
                          {formatChoicePreview(c.name, c.priceDelta)}
                          {c.maxQty > 1 ? ` · máx ${c.maxQty}` : ''}
                          {!c.isAvailable ? ' · agotado' : ''}
                        </Text>
                      </View>
                    ))}
                    {item.choices.length > chipCap ? (
                      <Text style={styles.choiceMore}>+{item.choices.length - chipCap}</Text>
                    ) : null}
                  </View>
                ) : (
                  <Text style={styles.noChoices}>Sin opciones todavía</Text>
                )}
              </View>
            </Pressable>
          );
        }}
      />

      <ModalShell
        visible={editorOpen}
        onClose={closeEditor}
        title={editingGroupId ? name.trim() || 'Editar grupo' : 'Nuevo grupo'}
        appearance="ticket"
        kicker="Catálogo"
        keyboardAvoiding
        widthVariant="comfortable"
        cardStyle={isModalDesktop ? styles.modalWideCard : styles.modalSheetCard}
        scrollRef={editorScrollRef}
        overlay={
          deactivateConfirmOpen ? (
            <OperationalConfirmModal
              visible
              presentation="overlay"
              title="Desactivar grupo"
              entityName={name.trim() || undefined}
              message={`Dejás de ofrecer estas opciones en ${linkedProductCount} producto${linkedProductCount === 1 ? '' : 's'}. Podés reactivarlo después.`}
              confirmLabel="Desactivar"
              destructive
              loading={togglingActive}
              onClose={() => setDeactivateConfirmOpen(false)}
              onConfirm={() => void runToggleActive()}
            />
          ) : null
        }
        scrollContentStyle={[
          styles.editorScrollContent,
          isModalDesktop ? styles.editorScrollContentWide : styles.editorScrollContentSheet,
        ]}
        footer={
          <View style={[styles.footerActions, !isModalDesktop && styles.footerActionsSheet]}>
            <TouchableOpacity
              style={[
                styles.footerBtn,
                styles.footerBtnSec,
                isModalDesktop ? styles.footerBtnDesktop : styles.footerBtnSheet,
              ]}
              onPress={closeEditor}
              disabled={saving || togglingActive}
              accessibilityRole="button"
            >
              <Text style={styles.footerBtnSecText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.footerBtn,
                styles.footerBtnPri,
                isModalDesktop ? styles.footerBtnDesktop : styles.footerBtnSheet,
                saving && styles.btnDisabled,
              ]}
              onPress={() => void handleSave()}
              disabled={saving || togglingActive || !canEditMenuOptions}
              accessibilityRole="button"
            >
              <Text style={styles.footerBtnPriText}>
                {saving ? 'Guardando…' : isModalDesktop ? 'Guardar grupo' : 'Guardar'}
              </Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.editorRoot}>
          <View style={[styles.strip, isModalDesktop && styles.stripWide]}>
            <View style={styles.stripIdentity}>
              <View style={styles.stripTitleRow}>
                <Text style={styles.stripLabel}>Nombre</Text>
                <Text style={styles.requiredBadge}>*</Text>
                {editingGroupId ? (
                  <TouchableOpacity
                    onPress={handleToggleActive}
                    disabled={saving || togglingActive}
                    accessibilityRole="button"
                    accessibilityLabel={
                      editingActive ? 'Desactivar grupo' : 'Reactivar grupo'
                    }
                    hitSlop={8}
                    style={styles.stripActiveBtn}
                  >
                    <StatusPill
                      tone={editingActive ? 'success' : 'danger'}
                      label={
                        togglingActive ? '…' : editingActive ? 'Activo' : 'Inactivo'
                      }
                    />
                  </TouchableOpacity>
                ) : null}
              </View>
              <TextInput
                style={[modalExperience.formInput, styles.nameInput]}
                value={name}
                onChangeText={setName}
                placeholder="Tamaño / Extras / Guarnición"
                placeholderTextColor={colors.textMuted}
                autoFocus={!editingGroupId && !isModalDesktop}
              />
            </View>

            <View style={styles.stripRules}>
              <Text style={styles.stripLabel}>Cuántos elige</Text>
              <View style={styles.seg}>
                {SELECTION_PRESETS.map((preset) => {
                  const on =
                    selectionMin === preset.min && selectionMax === preset.max;
                  return (
                    <TouchableOpacity
                      key={preset.id}
                      style={[styles.segBtn, on && styles.segBtnOn]}
                      onPress={() => {
                        setSelectionMin(preset.min);
                        setSelectionMax(preset.max);
                      }}
                      accessibilityRole="button"
                      accessibilityState={{ selected: on }}
                    >
                      <Text style={[styles.segBtnText, on && styles.segBtnTextOn]}>
                        {isModalDesktop ? preset.label : preset.shortLabel}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {showMinMax ? (
                <View style={styles.rulesRow}>
                  <View style={styles.rulesHalf}>
                    <ModalFormField label="Mínimo">
                      <TextInput
                        style={modalExperience.formInput}
                        value={selectionMin}
                        onChangeText={setSelectionMin}
                        keyboardType="number-pad"
                        placeholder="0"
                        placeholderTextColor={colors.textMuted}
                      />
                    </ModalFormField>
                  </View>
                  <View style={styles.rulesHalf}>
                    <ModalFormField label="Máximo">
                      <TextInput
                        style={modalExperience.formInput}
                        value={selectionMax}
                        onChangeText={setSelectionMax}
                        keyboardType="number-pad"
                        placeholder="1"
                        placeholderTextColor={colors.textMuted}
                      />
                    </ModalFormField>
                  </View>
                </View>
              ) : null}
            </View>
          </View>

          <View style={isModalDesktop ? styles.editorWide : styles.editorStack}>
            <View style={[styles.panel, isModalDesktop ? styles.mainCol : undefined]}>
              <View style={styles.panelHead}>
                <Text style={styles.panelEyebrow}>Opciones</Text>
                <Text style={styles.panelCount}>{choiceDrafts.length}</Text>
              </View>
              {isModalDesktop ? (
                <Text style={styles.panelHint}>
                  Armá las opciones. Si vinculás un insumo, descuenta stock al vender.
                </Text>
              ) : null}
              <View style={styles.optList}>
                {choiceDrafts.map((c) => {
                  const groupMax = Math.max(1, Number(selectionMax) || 1);
                  const showChoiceMax = groupMax > 1 || Number(c.maxQty) > 1;
                  const linkedName = c.linkedProductId
                    ? products.find((p) => p.id === c.linkedProductId)?.name
                    : null;
                  const hasSubstituteCandidates = choiceDrafts.some(
                    (d) => d.id && d.key !== c.key,
                  );
                  const substituteName = c.substitutesChoiceId
                    ? (choiceDrafts.find((d) => d.id === c.substitutesChoiceId)?.name ??
                      null)
                    : null;
                  const substituteProductName = c.substitutesProductId
                    ? (products.find((p) => p.id === c.substitutesProductId)?.name ??
                      null)
                    : null;
                  return (
                    <View
                      key={c.key}
                      style={[styles.optCard, !c.isAvailable && styles.optCardOff]}
                    >
                      <View
                        style={
                          isModalDesktop ? styles.optMainWide : styles.optMainStack
                        }
                      >
                        <TextInput
                          style={[
                            modalExperience.formInput,
                            styles.optNameInput,
                            !isModalDesktop && styles.optNameInputSheet,
                          ]}
                          value={c.name}
                          onChangeText={(t) => updateChoice(c.key, { name: t })}
                          placeholder="Nombre"
                          placeholderTextColor={colors.textMuted}
                        />
                        <View
                          style={[
                            styles.optMetaRow,
                            !isModalDesktop && styles.optMetaRowSheet,
                          ]}
                        >
                          <View
                            style={[
                              styles.deltaField,
                              !isModalDesktop && styles.deltaFieldSheet,
                            ]}
                          >
                            <Text style={styles.deltaLabel}>+$</Text>
                            <TextInput
                              style={[
                                styles.deltaInput,
                                { width: deltaFieldInputWidth(c.priceDelta) },
                              ]}
                              value={c.priceDelta}
                              onChangeText={(t) =>
                                updateChoice(c.key, { priceDelta: t })
                              }
                              keyboardType="numeric"
                              placeholder="0"
                              placeholderTextColor={colors.textMuted}
                              editable={canEditMenuOptions && canEditPrice}
                            />
                          </View>
                          {showChoiceMax ? (
                            <View
                              style={[
                                styles.deltaField,
                                !isModalDesktop && styles.deltaFieldSheet,
                              ]}
                            >
                              <Text style={styles.deltaLabel}>Máx</Text>
                              <TextInput
                                style={[
                                  styles.deltaInput,
                                  { width: deltaFieldInputWidth(c.maxQty) },
                                ]}
                                value={c.maxQty}
                                onChangeText={(t) =>
                                  updateChoice(c.key, { maxQty: t })
                                }
                                keyboardType="number-pad"
                                placeholder="1"
                                placeholderTextColor={colors.textMuted}
                              />
                            </View>
                          ) : null}
                          <TouchableOpacity
                            style={[
                              styles.availRow,
                              !isModalDesktop && styles.availRowSheet,
                            ]}
                            onPress={() =>
                              updateChoice(c.key, { isAvailable: !c.isAvailable })
                            }
                            accessibilityRole="switch"
                            accessibilityState={{ checked: c.isAvailable }}
                            accessibilityLabel={
                              c.isAvailable
                                ? 'Marcar opción agotada'
                                : 'Marcar opción a la venta'
                            }
                          >
                            {/* El Switch es display; la fila entera togglea (44px). */}
                            <View pointerEvents="none">
                              <Switch
                                value={c.isAvailable}
                                trackColor={{
                                  false: colors.borderSoft,
                                  true: colors.success,
                                }}
                                thumbColor={colors.surface}
                                style={!isModalDesktop ? styles.availSwitchSheet : undefined}
                              />
                            </View>
                            <Text
                              style={[
                                styles.availLabel,
                                c.isAvailable ? styles.availOn : styles.availOff,
                              ]}
                              numberOfLines={1}
                            >
                              {c.isAvailable ? 'A la venta' : 'Agotado'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                      {!c.isAvailable ? (
                        <Text style={styles.availHint}>
                          Agotado no se puede elegir al vender — reactivála cuando vuelva.
                        </Text>
                      ) : null}
                      <View style={styles.optFoot}>
                        <TouchableOpacity
                          style={[styles.insumoBtn, !isModalDesktop && styles.insumoBtnSheet]}
                          disabled={products.length === 0}
                          onPress={() =>
                            setChoiceFieldPicker({ choiceKey: c.key, field: 'linked' })
                          }
                          accessibilityRole="button"
                          accessibilityLabel="Elegir insumo vinculado"
                          testID={`opt-linked-open-${c.key}`}
                        >
                          <View
                            style={[
                              styles.insumoDot,
                              linkedName ? styles.insumoDotOn : null,
                            ]}
                          />
                          <Text style={styles.insumoPrefix}>Insumo · </Text>
                          <Text
                            style={[
                              styles.insumoValue,
                              !linkedName && styles.insumoValueMuted,
                            ]}
                            numberOfLines={1}
                          >
                            {products.length === 0
                              ? 'Sin productos en el catálogo'
                              : linkedName ?? 'Sin vincular'}
                          </Text>
                          {products.length > 0 ? (
                            <Ionicons
                              name="chevron-down"
                              size={14}
                              color={colors.textMuted}
                            />
                          ) : null}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.choiceIconBtn}
                          onPress={() => removeChoice(c.key)}
                          disabled={choiceDrafts.length <= 1}
                          accessibilityRole="button"
                          accessibilityLabel="Quitar opción"
                        >
                          <Ionicons
                            name="trash-outline"
                            size={18}
                            color={
                              choiceDrafts.length <= 1
                                ? colors.textMuted
                                : colors.textSecondary
                            }
                          />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.optMetaFoot}>
                        <TouchableOpacity
                          style={[
                            styles.insumoBtn,
                            styles.optMetaBtn,
                            !isModalDesktop && styles.optMetaBtnSheet,
                          ]}
                          onPress={() =>
                            setChoiceFieldPicker({ choiceKey: c.key, field: 'modifierKind' })
                          }
                          accessibilityRole="button"
                          accessibilityLabel="Elegir tipo de opción"
                          testID={`opt-kind-open-${c.key}`}
                        >
                          <Text style={styles.insumoPrefix}>Tipo · </Text>
                          <Text
                            style={[
                              styles.insumoValue,
                              !c.modifierKind && styles.insumoValueMuted,
                            ]}
                            numberOfLines={1}
                          >
                            {MODIFIER_KIND_LABEL[c.modifierKind]}
                          </Text>
                          <Ionicons
                            name="chevron-down"
                            size={14}
                            color={colors.textMuted}
                          />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.insumoBtn,
                            styles.optMetaBtn,
                            !isModalDesktop && styles.optMetaBtnSheet,
                          ]}
                          disabled={!hasSubstituteCandidates}
                          onPress={() =>
                            setChoiceFieldPicker({ choiceKey: c.key, field: 'substituteChoice' })
                          }
                          accessibilityRole="button"
                          accessibilityLabel="Elegir opción que sustituye"
                          testID={`opt-sub-open-${c.key}`}
                        >
                          <Text style={styles.insumoPrefix}>Sustituye · </Text>
                          <Text
                            style={[
                              styles.insumoValue,
                              !substituteName && styles.insumoValueMuted,
                            ]}
                            numberOfLines={1}
                          >
                            {hasSubstituteCandidates
                              ? (substituteName ?? 'Ninguna')
                              : 'Guardá el grupo primero'}
                          </Text>
                          {hasSubstituteCandidates ? (
                            <Ionicons
                              name="chevron-down"
                              size={14}
                              color={colors.textMuted}
                            />
                          ) : null}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.insumoBtn,
                            styles.optMetaBtn,
                            !isModalDesktop && styles.optMetaBtnSheet,
                          ]}
                          disabled={products.length === 0}
                          onPress={() =>
                            setChoiceFieldPicker({
                              choiceKey: c.key,
                              field: 'substituteProduct',
                            })
                          }
                          accessibilityRole="button"
                          accessibilityLabel="Elegir componente de combo que reemplaza"
                          testID={`opt-subprod-open-${c.key}`}
                        >
                          <Text style={styles.insumoPrefix}>Reemplaza · </Text>
                          <Text
                            style={[
                              styles.insumoValue,
                              !substituteProductName && styles.insumoValueMuted,
                            ]}
                            numberOfLines={1}
                          >
                            {products.length === 0
                              ? 'Sin productos en el catálogo'
                              : (substituteProductName ?? 'Ninguno')}
                          </Text>
                          {products.length > 0 ? (
                            <Ionicons
                              name="chevron-down"
                              size={14}
                              color={colors.textMuted}
                            />
                          ) : null}
                        </TouchableOpacity>
                      </View>
                      <View style={styles.optMetaFoot}>
                        <TextInput
                          style={[modalExperience.formInput, styles.optMetaInput]}
                          value={c.kitchenLabel}
                          onChangeText={(t) => updateChoice(c.key, { kitchenLabel: t })}
                          placeholder="En cocina (ej: S/cebolla)"
                          placeholderTextColor={colors.textMuted}
                          testID={`opt-kitchen-${c.key}`}
                        />
                        <TextInput
                          style={[modalExperience.formInput, styles.optMetaInput]}
                          value={c.allergenTags}
                          onChangeText={(t) => updateChoice(c.key, { allergenTags: t })}
                          placeholder="Alérgenos: gluten, lactosa"
                          placeholderTextColor={colors.textMuted}
                          testID={`opt-allergens-${c.key}`}
                        />
                      </View>
                    </View>
                  );
                })}
              </View>
              <TouchableOpacity
                style={styles.addChoiceBtn}
                onPress={addChoice}
                accessibilityRole="button"
              >
                <Ionicons name="add" size={18} color={colors.accent} />
                <Text style={styles.addChoiceText}>Agregar opción</Text>
              </TouchableOpacity>
            </View>

            {productPicker}
          </View>
        </View>
      </ModalShell>

      {choicePickerSheet ? (
        <ModalNestedPicker
          visible
          title={choicePickerSheet.title}
          options={choicePickerSheet.options}
          selectedValue={choicePickerSheet.selectedValue}
          onSelect={handleChoicePickerSelect}
          onClose={() => setChoiceFieldPicker(null)}
        />
      ) : null}
    </ShellContentFrame>
  );
}

/** Proto Square: borde de panel/control (rgba stone 0.14). */
const styles = StyleSheet.create({
  shell: {
    ...ticketSurfaces.screen,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  shellCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  listContent: {
    paddingBottom: spacing.xl,
    gap: 12,
    flexGrow: 1,
  },
  listHeader: {
    width: '100%',
    marginBottom: 4,
  },
  listColumns: {
    gap: 12,
  },
  hero: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: 14,
    gap: 10,
  },
  heroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  heroRowStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  heroCopy: { flex: 1, minWidth: 0, gap: 4 },
  heroKicker: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: ticketColors.brandInk,
  },
  heroSub: {
    ...typography.caption,
    fontSize: 13.5,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  heroMeta: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  heroCta: {
    height: 44,
    minWidth: 132,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: colors.accent,
    borderWidth: 1,
    borderColor: colors.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexShrink: 0,
  },
  heroCtaFull: {
    alignSelf: 'stretch',
    minWidth: 0,
  },
  heroCtaText: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '700',
    color: colors.surface,
  },
  rowPress: { flex: 1 },
  rowPressWide: { minWidth: 0 },
  rowPressed: { opacity: 0.92 },
  groupCard: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: 14,
    gap: 10,
    minHeight: 88,
  },
  groupCardOff: {
    opacity: 0.72,
    backgroundColor: ticketColors.tile,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.textPrimary,
    flex: 1,
    minWidth: 0,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  metaText: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
    flexShrink: 1,
  },
  choiceChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  choiceChip: {
    maxWidth: '100%',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.tile,
  },
  choiceChipOff: { opacity: 0.55 },
  choiceChipText: {
    ...typography.caption,
    color: colors.textPrimary,
    fontWeight: '600',
  },
  choiceMore: {
    ...typography.caption,
    color: colors.textMuted,
    alignSelf: 'center',
    fontWeight: '700',
  },
  noChoices: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
  },
  modalWideCard: {
    // Más ancho que «comfortable»: las filas de opción (tipo/sustituye/reemplaza)
    // necesitaban ~3 columnas legibles a 1280px.
    maxWidth: 1040,
    width: '100%',
    borderRadius: 18,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 0,
    gap: 0,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  /** Bottom sheet: no pelear el padding/radius del ModalShell mobile. */
  modalSheetCard: {
    paddingHorizontal: 0,
    gap: 0,
  },
  editorScrollContent: {
    gap: spacing.md,
    paddingBottom: spacing.lg,
    backgroundColor: colors.background,
  },
  editorScrollContentWide: {
    // Full-bleed mint under header/footer (card ya tiene padding 20).
    marginHorizontal: -20,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: spacing.lg,
  },
  editorScrollContentSheet: {
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: spacing.md,
    gap: 12,
  },
  editorRoot: {
    gap: spacing.md,
  },
  editorStack: {
    gap: spacing.md,
  },
  editorWide: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.md,
  },
  mainCol: {
    flex: 1.25,
    minWidth: 0,
  },
  sideCol: {
    flex: 1,
    minWidth: 0,
  },

  strip: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: 14,
    gap: spacing.md,
    flexDirection: 'column',
  },
  stripWide: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stripIdentity: {
    flex: 1.15,
    minWidth: 0,
    gap: 6,
  },
  stripRules: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  stripTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  stripActiveBtn: {
    marginLeft: 'auto' as const,
  },
  stripLabel: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  /** Requerido = «*» rojo, misma convención que ModalFormField. */
  requiredBadge: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.bad,
  },
  nameInput: {
    fontWeight: '700',
    borderColor: colors.controlBorder,
    minHeight: 42,
  },
  seg: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: colors.surfaceSecondary,
    padding: 4,
    borderRadius: 10,
  },
  segBtn: {
    flex: 1,
    minWidth: 0,
    minHeight: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  segBtnOn: {
    backgroundColor: colors.accent,
  },
  segBtnText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textSecondary,
    textAlign: 'center',
  },
  segBtnTextOn: {
    color: colors.surface,
  },
  rulesRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: 4,
  },
  rulesHalf: { flex: 1, minWidth: 0 },

  panel: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    overflow: 'hidden',
    paddingBottom: spacing.sm,
  },
  panelHead: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
  },
  panelEyebrow: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  panelCount: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  panelHint: {
    ...typography.caption,
    color: colors.textSecondary,
    lineHeight: 18,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
  },

  optList: {
    paddingHorizontal: spacing.sm,
    gap: spacing.sm,
  },
  optCard: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    backgroundColor: ticketColors.tile,
    padding: 10,
    gap: 8,
  },
  optCardOff: {
    opacity: 0.72,
    backgroundColor: colors.surfaceSecondary,
  },
  optMainWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  optMainStack: {
    gap: spacing.sm,
  },
  optNameInput: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
    fontWeight: '700',
    borderColor: ticketColors.rule,
    minHeight: 38,
    backgroundColor: colors.surface,
  },
  optNameInputSheet: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    alignSelf: 'stretch',
  },
  optMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  /** Proto sheet: +$ a la izquierda, switch a la derecha. */
  optMetaRowSheet: {
    width: '100%',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    gap: 10,
  },
  deltaField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 38,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: colors.surface,
    minWidth: 88,
    flexGrow: 0,
    flexShrink: 0,
  },
  /** No fijar maxWidth: el input crece con el monto (hasta ~7 dígitos). */
  deltaFieldSheet: {
    minWidth: 0,
  },
  deltaLabel: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  deltaInput: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
    minWidth: 52,
    paddingVertical: 6,
    paddingHorizontal: 0,
    fontVariant: ['tabular-nums'],
  },
  availRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexGrow: 0,
    flexShrink: 0,
    minHeight: 44,
  },
  availRowSheet: {
    justifyContent: 'flex-end',
  },
  availSwitchSheet: {
    transform: [{ scaleX: 0.88 }, { scaleY: 0.88 }],
  },
  availLabel: {
    ...typography.caption,
    fontWeight: '700',
    flexShrink: 0,
  },
  availOn: { color: colors.success },
  availOff: { color: colors.danger },
  /** Semántica de «Agotado»: contextual, solo visible cuando aplica. */
  availHint: {
    ...typography.caption,
    fontSize: 11.5,
    color: colors.textMuted,
  },
  optFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  insumoBtn: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  /** Sheet mobile: select de insumo con target ≥44px. */
  insumoBtnSheet: {
    minHeight: 44,
  },
  insumoDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.controlBorder,
  },
  insumoDotOn: {
    backgroundColor: colors.accent,
  },
  insumoPrefix: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  insumoValue: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textPrimary,
    flexShrink: 1,
  },
  insumoValueMuted: {
    color: colors.textMuted,
    fontWeight: '600',
  },
  optMetaFoot: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    rowGap: 4,
    marginTop: 2,
  },
  /**
   * Tipo / Sustituye / Reemplaza: wrap con base mínima para que el label
   * no se corte («Sustituye · Guardá el grupo primero» entra completo).
   */
  optMetaBtn: {
    flexGrow: 1,
    flexBasis: 190,
    minWidth: 190,
  },
  /**
   * Sheet mobile: selects de una línea completa (stack), no ~105px al lado
   * del ícono de borrar — el tap chico era inusable.
   */
  optMetaBtnSheet: {
    flexBasis: '100%',
    minWidth: 0,
    minHeight: 44,
  },
  optMetaInput: {
    flex: 1,
    minWidth: 0,
    minHeight: 34,
    paddingVertical: 6,
    fontSize: 12,
  },
  choiceIconBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    flexShrink: 0,
    marginVertical: -8,
  },
  addChoiceBtn: {
    marginTop: spacing.sm,
    marginHorizontal: spacing.sm,
    minHeight: 40,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.controlBorder,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  addChoiceText: {
    ...typography.body,
    fontWeight: '700',
    color: colors.accent,
  },

  prodTools: {
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
    paddingBottom: spacing.xs,
  },
  searchInput: {
    marginBottom: 0,
    minHeight: 38,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.tile,
  },
  bulkRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  bulkBtn: {
    height: 30,
    paddingHorizontal: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bulkBtnText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  productList: {
    marginHorizontal: spacing.sm,
    overflow: 'hidden',
  },
  productRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  productRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  productRowOn: {
    backgroundColor: colors.accentSoft,
  },
  check: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: colors.inputBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkOn: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  productName: {
    ...typography.body,
    fontSize: 13.5,
    fontWeight: '600',
    color: colors.textPrimary,
    flex: 1,
  },
  productNameOn: {
    fontWeight: '600',
  },
  emptyProducts: {
    ...typography.body,
    color: colors.textMuted,
    padding: spacing.md,
  },
  footerActions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-end',
    paddingTop: 4,
    paddingBottom: 16,
    backgroundColor: colors.surface,
  },
  footerActionsSheet: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  footerBtn: {
    height: 44,
    flex: 1,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  footerBtnDesktop: {
    minWidth: 120,
    maxWidth: 220,
  },
  footerBtnSheet: {
    minWidth: 0,
  },
  footerBtnSec: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  footerBtnSecText: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  footerBtnPri: {
    backgroundColor: colors.accent,
    borderWidth: 1,
    borderColor: colors.accent,
  },
  footerBtnPriText: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '700',
    color: colors.surface,
  },
  btnDisabled: { opacity: 0.6 },
});
